import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  IconButton,
  Snackbar
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  BugReport as TestIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  Delete as DeleteIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import axios from 'axios';
import { buildProfileQuestionsMap, formatQuotaCriteriaSummary, formatQuotaDynamicConditions } from '../../utils/quotaDisplay';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Функция перевода категорий
const translateCategory = (category) => {
  const translations = {
    'online-surveys': 'Онлайн-опросы',
    'online-interviews': 'Онлайн-интервью и фокус-группы',
    'website-app-testing': 'Тест сайтов и приложений',
    'store-visits': 'Визиты в магазины и офисы',
    'product-testing': 'Тестирование продуктов и услуг',
    'other': 'Другое',
    // Старые категории для обратной совместимости
    'general': 'Онлайн-опросы',
    'market': 'Онлайн-опросы',
    'market-research': 'Онлайн-опросы',
  };
  return translations[category] || category;
};

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Moderation() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pending surveys
  const [pendingSurveys, setPendingSurveys] = useState([]);
  const [activeSurveys, setActiveSurveys] = useState([]);
  const [completedSurveys, setCompletedSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyDialogOpen, setSurveyDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionError, setRejectionError] = useState(false);

  // Suspicious responses
  const [suspiciousResponses, setSuspiciousResponses] = useState([]);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);

  // Users cache
  const [usersCache, setUsersCache] = useState({});
  
  // Surveys cache
  const [surveysCache, setSurveysCache] = useState({});
  
  // Link copied snackbar
  const [linkCopied, setLinkCopied] = useState(false);
  const [profileQuestionsMap, setProfileQuestionsMap] = useState({});

  useEffect(() => {
    fetchPendingSurveys();
    fetchActiveSurveys();
    fetchCompletedSurveys();
    fetchSuspiciousResponses();
    fetchProfileQuestions();
  }, []);

  const fetchProfileQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/surveys/profile/questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileQuestionsMap(buildProfileQuestionsMap(res.data?.questions || []));
    } catch (err) {
      console.warn('Failed to fetch profile questions for quota display:', err?.response?.data || err.message);
    }
  };

  const fetchUserInfo = async (userId) => {
    if (usersCache[userId]) {
      return usersCache[userId];
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const user = response.data;
      setUsersCache(prev => ({ ...prev, [userId]: user }));
      return user;
    } catch (err) {
      console.error('Error fetching user:', err);
      return null;
    }
  };

  const fetchSurveyInfo = async (surveyId) => {
    if (surveysCache[surveyId]) {
      return surveysCache[surveyId];
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/surveys/${surveyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const survey = response.data;
      setSurveysCache(prev => ({ ...prev, [surveyId]: survey }));
      return survey;
    } catch (err) {
      console.error('Error fetching survey:', err);
      return null;
    }
  };

  const fetchPendingSurveys = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/surveys?status=pending_moderation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both old and new response formats
      const surveys = response.data.surveys || response.data;
      const surveysArray = Array.isArray(surveys) ? surveys : [];
      
      // Загружаем информацию о заказчиках
      const userIds = [...new Set(surveysArray.map(s => s.clientId).filter(Boolean))];
      await Promise.all(userIds.map(id => fetchUserInfo(id)));
      
      setPendingSurveys(surveysArray);
    } catch (err) {
      console.error('Error fetching pending surveys:', err);
      setError('Не удалось загрузить опросы на модерации');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveSurveys = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/surveys?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both old and new response formats
      const surveys = response.data.surveys || response.data;
      const surveysArray = Array.isArray(surveys) ? surveys : [];
      
      // Filter out profile surveys (isProfileSurvey=true) - they cannot be returned to moderation
      const nonProfileSurveys = surveysArray.filter(s => !s.isProfileSurvey);
      
      // Загружаем информацию о заказчиках
      const userIds = [...new Set(nonProfileSurveys.map(s => s.clientId).filter(Boolean))];
      await Promise.all(userIds.map(id => fetchUserInfo(id)));
      
      setActiveSurveys(nonProfileSurveys);
    } catch (err) {
      console.error('Error fetching active surveys:', err);
      setError('Не удалось загрузить активные опросы');
    }
  };

  const fetchCompletedSurveys = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/surveys?status=completed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both old and new response formats
      const surveys = response.data.surveys || response.data;
      const surveysArray = Array.isArray(surveys) ? surveys : [];
      
      // Загружаем информацию о заказчиках
      const userIds = [...new Set(surveysArray.map(s => s.clientId).filter(Boolean))];
      await Promise.all(userIds.map(id => fetchUserInfo(id)));
      
      setCompletedSurveys(surveysArray);
    } catch (err) {
      console.error('Error fetching completed surveys:', err);
      setError('Не удалось загрузить завершенные опросы');
    }
  };

  const fetchSuspiciousResponses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/surveys/responses/suspicious`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const responses = response.data;
      
      // Загружаем информацию о пользователях
      const userIds = [...new Set(responses.map(r => r.userId).filter(Boolean))];
      await Promise.all(userIds.map(id => fetchUserInfo(id)));
      
      // Загружаем информацию об опросах
      const surveyIds = [...new Set(responses.map(r => r.surveyId).filter(Boolean))];
      await Promise.all(surveyIds.map(id => fetchSurveyInfo(id)));
      
      setSuspiciousResponses(responses);
    } catch (err) {
      console.error('Error fetching suspicious responses:', err);
    }
  };

  const handleApproveSurvey = async (surveyId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/surveys/${surveyId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Опрос успешно одобрен');
      setSurveyDialogOpen(false);
      setRejectionReason('');
      setRejectionError(false);
      fetchPendingSurveys();
    } catch (err) {
      console.error('Error approving survey:', err);
      setError('Не удалось одобрить опрос');
    }
  };

  const handleRejectSurvey = async (surveyId) => {
    if (!rejectionReason.trim()) {
      setRejectionError(true);
      return;
    }

    try {
      setRejectionError(false);
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/surveys/${surveyId}/reject`, 
        { reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Опрос отклонен');
      setSurveyDialogOpen(false);
      setRejectionReason('');
      setRejectionError(false);
      fetchPendingSurveys();
    } catch (err) {
      console.error('Error rejecting survey:', err);
      setError('Не удалось отклонить опрос');
    }
  };

  const handleApproveResponse = async (responseId) => {
    try {
      const token = localStorage.getItem('token');
      // Нужен surveyId для корректного API
      const resp = selectedResponse || suspiciousResponses.find(r => r.id === responseId);
      if (!resp || !resp.surveyId) throw new Error('Не удалось определить surveyId для ответа');
      await axios.put(`${API_BASE}/surveys/${resp.surveyId}/responses/${responseId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Ответ одобрен');
      setResponseDialogOpen(false);
      fetchSuspiciousResponses();
    } catch (err) {
      console.error('Error approving response:', err);
      setError(err.response?.data?.error || 'Не удалось одобрить ответ');
    }
  };

  const handleRejectResponse = async (responseId) => {
    try {
      const token = localStorage.getItem('token');
      const reason = window.prompt('Укажите причину отклонения (необязательно):', '');
      const resp = selectedResponse || suspiciousResponses.find(r => r.id === responseId);
      if (!resp || !resp.surveyId) throw new Error('Не удалось определить surveyId для ответа');
      await axios.put(`${API_BASE}/surveys/${resp.surveyId}/responses/${responseId}/reject`, { reason: reason || '' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Ответ отклонен');
      setResponseDialogOpen(false);
      fetchSuspiciousResponses();
    } catch (err) {
      console.error('Error rejecting response:', err);
      setError(err.response?.data?.error || 'Не удалось отклонить ответ');
    }
  };

  const handleDeleteResponse = async (responseId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот ответ? Это действие нельзя отменить.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/surveys/responses/${responseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Ответ удалён');
      setResponseDialogOpen(false);
      fetchSuspiciousResponses();
    } catch (err) {
      console.error('Error deleting response:', err);
      setError(err.response?.data?.error || 'Не удалось удалить ответ');
    }
  };

  const handleForceCompleteSurvey = async (surveyId) => {
    if (!window.confirm('Вы уверены, что хотите завершить этот опрос? Он станет недоступен для новых респондентов.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/surveys/${surveyId}/force-complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Опрос завершён');
      fetchActiveSurveys();
      fetchCompletedSurveys();
    } catch (err) {
      console.error('Error force completing survey:', err);
      setError(err.response?.data?.error || 'Не удалось завершить опрос');
    }
  };

  const handleReturnToModeration = async (surveyId) => {
    if (!window.confirm('Вернуть опрос на модерацию? Он станет недоступен респондентам.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/surveys/${surveyId}/return-to-moderation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Опрос возвращён на модерацию');
      setSurveyDialogOpen(false);
      fetchActiveSurveys();
      fetchPendingSurveys(); // Refresh pending surveys list
    } catch (err) {
      console.error('Error returning survey to moderation:', err);
      setError('Не удалось вернуть опрос на модерацию');
    }
  };

  const openSurveyDetails = (survey) => {
    setSelectedSurvey(survey);
    setSurveyDialogOpen(true);
  };

  const openResponseDetails = (response) => {
    setSelectedResponse(response);
    setResponseDialogOpen(true);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{
          fontWeight: 700,
          color: '#2d3748',
          fontSize: { xs: '1.75rem', md: '2.125rem' },
          mb: 3
        }}
      >
        Модерация
      </Typography>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, borderRadius: '12px' }}>{success}</Alert>}

      <Paper 
        sx={{ 
          mb: 2,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '16px', md: '24px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}
      >
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              color: '#718096',
              transition: 'all 0.3s ease',
              '&.Mui-selected': {
                color: '#0984e3',
                fontWeight: 700
              },
              '&:hover': {
                color: '#0984e3',
                backgroundColor: 'rgba(116, 185, 255, 0.1)'
              }
            },
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(90deg, #74b9ff 0%, #0984e3 100%)',
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab label={`Опросы на модерации (${pendingSurveys.length})`} />
          <Tab label={`Активные опросы (${activeSurveys.length})`} />
          <Tab label={`Завершенные опросы (${completedSurveys.length})`} />
          <Tab label={`Подозрительные ответы (${suspiciousResponses.length})`} />
        </Tabs>
      </Paper>

      {/* Tab 0: Pending Surveys */}
      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : pendingSurveys.length === 0 ? (
          <Alert severity="info">Нет опросов на модерации</Alert>
        ) : (
          <TableContainer component={Paper} sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '16px', md: '24px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)'
                }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Название</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Категория</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Заказчик</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Дата создания</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Вопросов</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Квота</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingSurveys.map((survey) => (
                  <TableRow key={survey.id}>
                    <TableCell>
                      {survey.title}
                      {survey.isPublicLink && (
                        <Chip 
                          label="Публичная ссылка" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                      {survey.isExternalSurvey && (
                        <Chip 
                          label="Внешний опрос" 
                          size="small" 
                          color="warning" 
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{translateCategory(survey.category)}</TableCell>
                    <TableCell>
                      {usersCache[survey.clientId]?.fullName || survey.clientId || 'Неизвестно'}
                    </TableCell>
                    <TableCell>{new Date(survey.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>{survey.questions?.length || 0}</TableCell>
                    <TableCell>
                      {survey.responseCount || 0} / {survey.maxResponses || '∞'}
                    </TableCell>
                    <TableCell>
                      {survey.isPublicLink && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            const publicUrl = `${window.location.origin}/public/survey/${survey.customSlug || survey.id}`;
                            navigator.clipboard.writeText(publicUrl);
                            setLinkCopied(true);
                          }}
                          title="Скопировать публичную ссылку"
                          sx={{ mr: 1 }}
                        >
                          <LinkIcon />
                        </IconButton>
                      )}
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => openSurveyDetails(survey)}
                        sx={{ mr: 1 }}
                      >
                        Просмотр
                      </Button>
                      {survey.questions && survey.questions.length > 0 && (
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<TestIcon />}
                          onClick={() => navigate(`/admin/survey/${survey.id}/test`)}
                          sx={{ mr: 1 }}
                        >
                          Тестировать
                        </Button>
                      )}
                      {(survey.status === 'draft' || survey.status === 'rejected' || survey.status === 'pending_moderation') && (
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<EditIcon />}
                          onClick={() => navigate(`/admin/survey-builder/${survey.id}`)}
                        >
                          Редактировать
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Tab 1: Active Surveys */}
      <TabPanel value={tabValue} index={1}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : activeSurveys.length === 0 ? (
          <Alert severity="info">Нет активных опросов</Alert>
        ) : (
          <TableContainer component={Paper} sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '16px', md: '24px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)'
                }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Название</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Категория</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Заказчик</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Дата публикации</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ответов</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Квота</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeSurveys.map((survey) => (
                  <TableRow key={survey.id}>
                    <TableCell>
                      {survey.title}
                      {survey.isPublicLink && (
                        <Chip 
                          label="Публичная ссылка" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                      {survey.isExternalSurvey && (
                        <Chip 
                          label="Внешний опрос" 
                          size="small" 
                          color="warning" 
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{translateCategory(survey.category)}</TableCell>
                    <TableCell>
                      {usersCache[survey.clientId]?.fullName || survey.clientId || 'Неизвестно'}
                    </TableCell>
                    <TableCell>
                      {survey.publishedAt ? new Date(survey.publishedAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>{survey.responseCount || 0}</TableCell>
                    <TableCell>
                      {survey.responseCount || 0} / {survey.maxResponses || '∞'}
                    </TableCell>
                    <TableCell>
                      {survey.isPublicLink && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            const publicUrl = `${window.location.origin}/public/survey/${survey.customSlug || survey.id}`;
                            navigator.clipboard.writeText(publicUrl);
                            setLinkCopied(true);
                          }}
                          title="Скопировать публичную ссылку"
                          sx={{ mr: 1 }}
                        >
                          <LinkIcon />
                        </IconButton>
                      )}
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={() => navigate(`/admin/survey-builder/${survey.id}`)}
                        sx={{ mr: 1 }}
                      >
                        Редактировать
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<StopIcon />}
                        onClick={() => handleForceCompleteSurvey(survey.id)}
                        sx={{ mr: 1 }}
                      >
                        Завершить
                      </Button>
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => handleReturnToModeration(survey.id)}
                      >
                        Вернуть на модерацию
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Tab 2: Completed Surveys */}
      <TabPanel value={tabValue} index={2}>
        {completedSurveys.length === 0 ? (
          <Alert severity="info">Нет завершенных опросов</Alert>
        ) : (
          <TableContainer component={Paper} sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '16px', md: '24px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)'
                }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Название</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Категория</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Заказчик</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Дата публикации</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Дата завершения</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ответов</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Квота</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completedSurveys.map((survey) => (
                  <TableRow key={survey.id}>
                    <TableCell>
                      {survey.title}
                      {survey.isPublicLink && (
                        <Chip 
                          label="Публичная ссылка" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                      {survey.isExternalSurvey && (
                        <Chip 
                          label="Внешний опрос" 
                          size="small" 
                          color="warning" 
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{translateCategory(survey.category)}</TableCell>
                    <TableCell>
                      {usersCache[survey.clientId]?.fullName || survey.clientId || 'Неизвестно'}
                    </TableCell>
                    <TableCell>
                      {survey.publishedAt ? new Date(survey.publishedAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      {survey.completedAt ? new Date(survey.completedAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>{survey.responseCount || 0}</TableCell>
                    <TableCell>
                      {survey.responseCount || 0} / {survey.maxResponses || '∞'}
                    </TableCell>
                    <TableCell>
                      {survey.isPublicLink && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            const publicUrl = `${window.location.origin}/public/survey/${survey.customSlug || survey.id}`;
                            navigator.clipboard.writeText(publicUrl);
                            setLinkCopied(true);
                          }}
                          title="Скопировать публичную ссылку"
                          sx={{ mr: 1 }}
                        >
                          <LinkIcon />
                        </IconButton>
                      )}
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => openSurveyDetails(survey)}
                        sx={{ mr: 1 }}
                      >
                        Просмотр
                      </Button>
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={() => navigate(`/admin/survey-builder/${survey.id}`)}
                      >
                        Редактировать
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Tab 3: Suspicious Responses */}
      <TabPanel value={tabValue} index={3}>
        {suspiciousResponses.length === 0 ? (
          <Alert severity="info">Нет подозрительных ответов</Alert>
        ) : (
          <TableContainer component={Paper} sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '16px', md: '24px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)'
                }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ID ответа</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Опрос</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Пользователь</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Дата</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Причина</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suspiciousResponses.map((response) => (
                  <TableRow key={response.id}>
                    <TableCell>{response.id.substring(0, 8)}...</TableCell>
                    <TableCell>
                      {surveysCache[response.surveyId]?.title || response.surveyId?.substring(0, 8) + '...' || 'Неизвестно'}
                    </TableCell>
                    <TableCell>
                      {usersCache[response.userId]?.fullName || response.userId?.substring(0, 8) + '...' || 'Неизвестно'}
                    </TableCell>
                    <TableCell>{new Date(response.completedAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {response.flags?.fastCompletion && (
                        <Chip label="Быстрое прохождение" size="small" color="warning" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => openResponseDetails(response)}
                      >
                        Просмотр
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteResponse(response.id)}
                        title="Удалить ответ"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Survey Details Dialog */}
      <Dialog open={surveyDialogOpen} onClose={() => {
        setSurveyDialogOpen(false);
        setRejectionReason('');
        setRejectionError(false);
      }} maxWidth="md" fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle>Детали опроса</DialogTitle>
        <DialogContent>
          {selectedSurvey && (
            <Box>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{selectedSurvey.title}</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {selectedSurvey.description}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Категория</Typography>
                      <Typography variant="body1">{translateCategory(selectedSurvey.category)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Время прохождения</Typography>
                      <Typography variant="body1">{selectedSurvey.estimatedTime} мин</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Вознаграждение</Typography>
                      {(() => {
                        const r = selectedSurvey.reward || {};
                        const money = typeof r.moneyAmount === 'number' ? r.moneyAmount
                                      : (r.type === 'money' ? (r.amount || 0) : (r.money?.amount || 0));
                        const points = typeof r.pointsAmount === 'number' ? r.pointsAmount
                                       : (r.type === 'points' ? (r.amount || 0) : (r.points?.amount || 0));
                        return (
                          <>
                            <Typography variant="body1">{money || 0} BYN</Typography>
                            <Typography variant="body2" color="text.secondary">{points || 0} баллов</Typography>
                          </>
                        );
                      })()}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Макс. ответов</Typography>
                      <Typography variant="body1">{selectedSurvey.maxResponses || 'Неограничено'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Target Audience */}
              {selectedSurvey.targetAudience && Object.keys(selectedSurvey.targetAudience || {}).length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Целевая аудитория</Typography>
                    <Grid container spacing={2}>
                      {selectedSurvey.targetAudience.gender && selectedSurvey.targetAudience.gender.length > 0 && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Пол</Typography>
                          <Typography variant="body2">{selectedSurvey.targetAudience.gender.join(', ')}</Typography>
                        </Grid>
                      )}
                      {selectedSurvey.targetAudience.ageRange && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Возраст</Typography>
                          <Typography variant="body2">{selectedSurvey.targetAudience.ageRange.min} - {selectedSurvey.targetAudience.ageRange.max}</Typography>
                        </Grid>
                      )}
                      {selectedSurvey.targetAudience.regions && selectedSurvey.targetAudience.regions.length > 0 && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Регионы</Typography>
                          <Typography variant="body2">{selectedSurvey.targetAudience.regions.join(', ')}</Typography>
                        </Grid>
                      )}
                      {selectedSurvey.targetAudience.cities && selectedSurvey.targetAudience.cities.length > 0 && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Города</Typography>
                          <Typography variant="body2">{selectedSurvey.targetAudience.cities.join(', ')}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Вопросы:</Typography>
              {selectedSurvey.questions?.map((q, idx) => (
                <Card key={idx} sx={{ mb: 1 }}>
                  <CardContent>
                    <Typography variant="body2">
                      <strong>{idx + 1}. {q.text}</strong> ({q.type})
                    </Typography>
                    {q.options && (
                      <Typography variant="caption" color="text.secondary">
                        Варианты: {q.options.join(', ')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}

              <Divider sx={{ my: 2 }} />

              {/* Logic */}
              {selectedSurvey.logic && selectedSurvey.logic.length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Логика переходов</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{
                      background: 'rgba(255, 255, 255, 0.7)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.5)'
                    }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{
                            background: 'linear-gradient(135deg, rgba(116, 185, 255, 0.3) 0%, rgba(9, 132, 227, 0.3) 100%)'
                          }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Вопрос</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Если ответ</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Действие</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Цель</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedSurvey.logic.map((rule, index) => {
                            const q = selectedSurvey.questions?.find(x => x.id === rule.questionId);
                            const targetQ = selectedSurvey.questions?.find(x => x.id === rule.condition?.target);
                            const answersArr = Array.isArray(rule.condition?.answers) && rule.condition.answers.length > 0
                              ? rule.condition.answers
                              : (rule.condition?.answer ? [rule.condition.answer] : []);
                            const actionLabel = rule.condition?.action === 'skip' ? 'Пропустить'
                              : rule.condition?.action === 'show' ? 'Показать'
                              : 'Завершить';
                            return (
                              <TableRow key={index}>
                                <TableCell>{q?.text || 'N/A'}</TableCell>
                                <TableCell>{answersArr.join(', ')}</TableCell>
                                <TableCell>{actionLabel}</TableCell>
                                <TableCell>{rule.condition?.action === 'end' ? 'Завершить' : (targetQ?.text || 'N/A')}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* Quotas */}
              {selectedSurvey.quotas && selectedSurvey.quotas.length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Квоты</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{
                      background: 'rgba(255, 255, 255, 0.7)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.5)',
                      overflowX: 'auto',
                    }}>
                      <Table size="small" sx={{ minWidth: 640 }}>
                        <TableHead>
                          <TableRow sx={{
                            background: 'linear-gradient(135deg, rgba(116, 185, 255, 0.3) 0%, rgba(9, 132, 227, 0.3) 100%)'
                          }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Описание</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Критерии</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Динамические условия</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Лимит</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Заполнено</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedSurvey.quotas.map((quota, index) => (
                            <TableRow key={quota.id || index}>
                              <TableCell>{quota.description || '-'}</TableCell>
                              <TableCell>{formatQuotaCriteriaSummary(quota.criteria)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'pre-line' }}>
                                {formatQuotaDynamicConditions(quota.criteria, profileQuestionsMap)}
                              </TableCell>
                              <TableCell>{quota.limit}</TableCell>
                              <TableCell>{quota.filled || 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Причина отклонения (если требуется)"
                value={rejectionReason}
                onChange={(e) => {
                  setRejectionReason(e.target.value);
                  setRejectionError(false);
                }}
                margin="normal"
                error={rejectionError}
                helperText={rejectionError ? 'Укажите причину отклонения' : ''}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSurveyDialogOpen(false);
            setRejectionReason('');
            setRejectionError(false);
          }} sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}>Закрыть</Button>
          {selectedSurvey?.questions && selectedSurvey.questions.length > 0 && (
            <Button
              startIcon={<TestIcon />}
              onClick={() => {
                setSurveyDialogOpen(false);
                setRejectionReason('');
                setRejectionError(false);
                navigate(`/admin/survey/${selectedSurvey?.id}/test`);
              }}
              sx={{
                background: 'linear-gradient(135deg, #ff9800 0%, #ff5722 100%)',
                color: '#fff',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(255, 152, 0, 0.4)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Тестировать
            </Button>
          )}
          <Button
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/survey-builder/${selectedSurvey?.id}`)}
            sx={{
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              color: '#fff',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(116, 185, 255, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(116, 185, 255, 0.4)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Редактировать
          </Button>
          <Button
            startIcon={<RejectIcon />}
            onClick={() => handleRejectSurvey(selectedSurvey?.id)}
            sx={{
              background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
              color: '#fff',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(255, 118, 117, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(255, 118, 117, 0.4)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Отклонить
          </Button>
          <Button
            variant="contained"
            startIcon={<ApproveIcon />}
            onClick={() => handleApproveSurvey(selectedSurvey?.id)}
            sx={{
              background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
              color: '#fff',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(86, 171, 47, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(86, 171, 47, 0.4)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Одобрить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Response Details Dialog */}
      <Dialog open={responseDialogOpen} onClose={() => setResponseDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle>Детали ответа</DialogTitle>
        <DialogContent>
          {selectedResponse && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">ID ответа</Typography>
                  <Typography variant="body2">{selectedResponse.id}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Пользователь</Typography>
                  <Typography variant="body2">
                    {usersCache[selectedResponse.userId]?.fullName || selectedResponse.userId || 'Неизвестно'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Время прохождения</Typography>
                  <Typography variant="body2">{selectedResponse.timeSpent} сек</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">IP адрес</Typography>
                  <Typography variant="body2">{selectedResponse.ip}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Причины подозрения:</Typography>
              {selectedResponse.flags?.fastCompletion && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Быстрое прохождение (менее {selectedResponse.flags.thresholdSec} сек)
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Ответы:</Typography>
              {selectedResponse.answers && typeof selectedResponse.answers === 'object' ? (
                Object.entries(selectedResponse.answers).map(([questionId, answer], idx) => (
                  <Card key={idx} sx={{ mb: 1 }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Вопрос ID: {questionId}</Typography>
                      <Typography variant="body2">
                        {Array.isArray(answer) ? answer.join(', ') : String(answer)}
                      </Typography>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">Нет данных ответов</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)} sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}>Закрыть</Button>
          <Button
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteResponse(selectedResponse?.id)}
            sx={{
              background: 'linear-gradient(135deg, #636e72 0%, #2d3436 100%)',
              color: '#fff',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(99, 110, 114, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(99, 110, 114, 0.4)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Удалить
          </Button>
          <Button
            startIcon={<RejectIcon />}
            onClick={() => handleRejectResponse(selectedResponse?.id)}
            sx={{
              background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
              color: '#fff',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(255, 118, 117, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(255, 118, 117, 0.4)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Отклонить
          </Button>
          <Button
            variant="contained"
            startIcon={<ApproveIcon />}
            onClick={() => handleApproveResponse(selectedResponse?.id)}
            sx={{
              background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
              color: '#fff',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(86, 171, 47, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(86, 171, 47, 0.4)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Одобрить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link copied snackbar */}
      <Snackbar
        open={linkCopied}
        autoHideDuration={3000}
        onClose={() => setLinkCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setLinkCopied(false)} severity="success" sx={{ width: '100%' }}>
          Ссылка скопирована в буфер обмена!
        </Alert>
      </Snackbar>
    </Container>
  );
}

